import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { materielService, CreateMaterielDTO, MATERIEL_TYPES } from '@/services/materielService';
import type { MaterielDeneignement } from '@/types';
import toast from 'react-hot-toast';
import { getErrorMessage } from '@/services/api';
import { ConfirmModal } from '@/components/common/Modal';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  FunnelIcon,
  TruckIcon,
} from '@heroicons/react/24/outline';

/**
 * Page de gestion du materiel de deneigement
 */
export function MaterielPage() {
  const queryClient = useQueryClient();
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [disponibleFilter, setDisponibleFilter] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingMateriel, setEditingMateriel] = useState<MaterielDeneignement | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MaterielDeneignement | null>(null);
  const [toggleTarget, setToggleTarget] = useState<MaterielDeneignement | null>(null);

  // Chargement du materiel
  const { data: materiels, isLoading } = useQuery({
    queryKey: ['materiels', typeFilter, disponibleFilter],
    queryFn: () =>
      materielService.list({
        type: typeFilter || undefined,
        disponible: disponibleFilter === '' ? undefined : disponibleFilter === 'true',
      }),
  });

  // Stats
  const { data: stats } = useQuery({
    queryKey: ['materiels-stats'],
    queryFn: () => materielService.getStats(),
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: CreateMaterielDTO) => materielService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['materiels'] });
      queryClient.invalidateQueries({ queryKey: ['materiels-stats'] });
      setShowModal(false);
      toast.success('Materiel cree avec succes');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CreateMaterielDTO> }) =>
      materielService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['materiels'] });
      setShowModal(false);
      setEditingMateriel(null);
      toast.success('Materiel modifie avec succes');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => materielService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['materiels'] });
      queryClient.invalidateQueries({ queryKey: ['materiels-stats'] });
      toast.success('Materiel supprime');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const toggleDisponibiliteMutation = useMutation({
    mutationFn: ({ id, disponible }: { id: number; disponible: boolean }) =>
      materielService.updateDisponibilite(id, disponible),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['materiels'] });
      queryClient.invalidateQueries({ queryKey: ['materiels-stats'] });
      toast.success('Disponibilite mise a jour');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data: CreateMaterielDTO = {
      nom: formData.get('nom') as string,
      type: formData.get('type') as string,
      immatriculation: formData.get('immatriculation') as string || undefined,
      disponible: formData.get('disponible') === 'true',
    };

    if (editingMateriel) {
      updateMutation.mutate({ id: editingMateriel.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (materiel: MaterielDeneignement) => {
    setDeleteTarget(materiel);
  };

  const confirmDelete = () => {
    if (deleteTarget) {
      deleteMutation.mutate(deleteTarget.id);
      setDeleteTarget(null);
    }
  };

  const handleToggleDisponibilite = (materiel: MaterielDeneignement) => {
    setToggleTarget(materiel);
  };

  const confirmToggle = () => {
    if (toggleTarget) {
      toggleDisponibiliteMutation.mutate({
        id: toggleTarget.id,
        disponible: !toggleTarget.disponible,
      });
      setToggleTarget(null);
    }
  };

  const getTypeLabel = (type: string) => {
    const found = MATERIEL_TYPES.find((t) => t.value === type);
    return found?.label || type;
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
        <p className="text-sm text-gray-500 mt-3">Chargement du materiel...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Gestion du materiel</h2>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="btn-secondary"
          >
            <FunnelIcon className="w-5 h-5 mr-2" />
            Filtres
          </button>
          <button
            onClick={() => {
              setEditingMateriel(null);
              setShowModal(true);
            }}
            className="btn-primary"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Nouveau materiel
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card">
            <div className="card-body text-center">
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              <p className="text-sm text-gray-500">Total</p>
            </div>
          </div>
          <div className="card">
            <div className="card-body text-center">
              <p className="text-2xl font-bold text-green-600">{stats.disponibles}</p>
              <p className="text-sm text-gray-500">Disponibles</p>
            </div>
          </div>
          <div className="card">
            <div className="card-body text-center">
              <p className="text-2xl font-bold text-red-600">{stats.indisponibles}</p>
              <p className="text-sm text-gray-500">Indisponibles</p>
            </div>
          </div>
          <div className="card">
            <div className="card-body text-center">
              <p className="text-2xl font-bold text-primary-600">
                {stats.parType ? Object.keys(stats.parType).length : 0}
              </p>
              <p className="text-sm text-gray-500">Types</p>
            </div>
          </div>
        </div>
      )}

      {/* Filtres */}
      {showFilters && (
        <div className="card">
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="label">Type</label>
                <select
                  className="input"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <option value="">Tous les types</option>
                  {MATERIEL_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Disponibilite</label>
                <select
                  className="input"
                  value={disponibleFilter}
                  onChange={(e) => setDisponibleFilter(e.target.value)}
                >
                  <option value="">Tous</option>
                  <option value="true">Disponible</option>
                  <option value="false">Indisponible</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => {
                    setTypeFilter('');
                    setDisponibleFilter('');
                  }}
                  className="btn-secondary w-full"
                >
                  Reinitialiser
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Grille de materiels */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {materiels?.map((materiel) => (
          <div key={materiel.id} className="card">
            <div className="card-body">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center">
                  <div
                    className={`p-2 rounded-lg mr-3 ${
                      materiel.disponible ? 'bg-green-100' : 'bg-red-100'
                    }`}
                  >
                    <TruckIcon
                      className={`w-6 h-6 ${
                        materiel.disponible ? 'text-green-600' : 'text-red-600'
                      }`}
                    />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{materiel.nom}</h3>
                    <p className="text-sm text-gray-500">{getTypeLabel(materiel.type)}</p>
                  </div>
                </div>
              </div>

              {materiel.immatriculation && (
                <p className="text-sm text-gray-600 mb-3">
                  Immat: <span className="font-mono">{materiel.immatriculation}</span>
                </p>
              )}

              <div className="flex items-center justify-between pt-3 border-t">
                <button
                  onClick={() => handleToggleDisponibilite(materiel)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                    materiel.disponible
                      ? 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200'
                      : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
                  }`}
                >
                  {materiel.disponible ? 'Marquer indisponible' : 'Marquer disponible'}
                </button>

                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => {
                      setEditingMateriel(materiel);
                      setShowModal(true);
                    }}
                    className="p-1 text-gray-400 hover:text-primary-500"
                    title="Modifier"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(materiel)}
                    className="p-1 text-gray-400 hover:text-red-500"
                    title="Supprimer"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {materiels?.length === 0 && (
        <div className="text-center py-12">
          <TruckIcon className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">Aucun materiel trouve</p>
        </div>
      )}

      {/* Modale confirmation suppression */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title={`Supprimer le materiel "${deleteTarget?.nom}" ?`}
        message="Cette action est irreversible. Le materiel sera definitivement supprime."
        confirmLabel="Supprimer"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />

      {/* Modale confirmation toggle disponibilite */}
      <ConfirmModal
        isOpen={!!toggleTarget}
        onClose={() => setToggleTarget(null)}
        onConfirm={confirmToggle}
        title={toggleTarget?.disponible ? 'Marquer indisponible ?' : 'Marquer disponible ?'}
        message={toggleTarget
          ? `Changer la disponibilite de "${toggleTarget.nom}" vers ${toggleTarget.disponible ? '"Indisponible"' : '"Disponible"'} ?`
          : ''}
        confirmLabel="Confirmer"
        variant="warning"
        isLoading={toggleDisponibiliteMutation.isPending}
      />

      {/* Modal creation/edition */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingMateriel ? 'Modifier le materiel' : 'Nouveau materiel'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="label">Nom *</label>
                <input
                  type="text"
                  name="nom"
                  defaultValue={editingMateriel?.nom}
                  className="input"
                  required
                />
              </div>
              <div>
                <label className="label">Type *</label>
                <select
                  name="type"
                  defaultValue={editingMateriel?.type || 'chasse_neige'}
                  className="input"
                  required
                >
                  {MATERIEL_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Immatriculation</label>
                <input
                  type="text"
                  name="immatriculation"
                  defaultValue={editingMateriel?.immatriculation}
                  className="input"
                  placeholder="AB-123-CD"
                />
              </div>
              <div>
                <label className="label">Disponibilite</label>
                <select
                  name="disponible"
                  defaultValue={editingMateriel?.disponible !== false ? 'true' : 'false'}
                  className="input"
                >
                  <option value="true">Disponible</option>
                  <option value="false">Indisponible</option>
                </select>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingMateriel(null);
                  }}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  {editingMateriel ? 'Modifier' : 'Creer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default MaterielPage;
